// src/slices/preferencesSlice.js

import { createSlice, createSelector } from '@reduxjs/toolkit';

// Define default preferences
const defaultPreferences = {
  newUsersOverTimeDays: 28, // 4 weeks
  activeUsersDays: 30, // recent-activity window (active users/forecasters, new registrations)
  resource: null,
  blurSensitive: false,
  theme: 'system', // Follow OS dark/light preference by default
  preferredModel: 'weighted_avg', // Default model for sessions
  seenVersion: null, // null = no version has been dismissed yet
  sidebarCollapsed: false,
  insightsEnabled: false, // Ensemble insights layer on the dashboard chart
  insightsStrongOnly: true,
  insightsShowPast: false,
  // Observed ramps (measured, not pointers) are off until asked for.
  insightsTypes: { ramp_down: true, ramp_up: true, split: true, revision: true, crowd: true, observed: false },
  insightsRampMethod: 'forecast', // which ramp pointers the API serves (ramps=forecast|crowd)
  // Notifications preferences: emails and toggles for different notification types
};

// Function to load preferences from localStorage
const loadPreferences = () => {
  try {
    const serializedPrefs = localStorage.getItem('userPreferences');
    if (serializedPrefs === null) {
      return defaultPreferences;
    }
    return JSON.parse(serializedPrefs);
  } catch (err) {
    console.error('Could not load preferences from localStorage:', err);
    return defaultPreferences;
  }
};
// Function to save preferences to localStorage
const savePreferences = (preferences) => {
  try {
    const serializedPrefs = JSON.stringify(preferences);
    localStorage.setItem('userPreferences', serializedPrefs);
  } catch (err) {
    console.error('Could not save preferences to localStorage:', err);
  }
};

const DEFAULT_INSIGHT_TYPES = { ramp_down: true, ramp_up: true, split: true, revision: true, crowd: true, observed: false };
export const RAMP_METHODS = ['forecast', 'crowd'];

const preferencesSlice = createSlice({
  name: 'preferences',
  initialState: loadPreferences(),
  reducers: {
    setNewUsersOverTimeDays(state, action) {
      state.newUsersOverTimeDays = action.payload;
      savePreferences(state);
    },
    setActiveUsersDays(state, action) {
      state.activeUsersDays = action.payload;
      savePreferences(state);
    },
    setSelectedResource(state, action) {
      state.resource = action.payload; // Store only the resource ID
      savePreferences(state);
    },
    setBlurSensitive(state, action) {
      state.blurSensitive = action.payload;
      savePreferences(state);
    },
    setTheme(state, action) {
      state.theme = action.payload;
      savePreferences(state);
    },
    setPreferredModel(state, action) {
      state.preferredModel = action.payload;
      savePreferences(state);
    },
    setInsightsEnabled(state, action) {
      state.insightsEnabled = action.payload;
      savePreferences(state);
    },
    setInsightsStrongOnly(state, action) {
      state.insightsStrongOnly = action.payload;
      savePreferences(state);
    },
    setInsightsShowPast(state, action) {
      state.insightsShowPast = action.payload;
      savePreferences(state);
    },
    setInsightsType(state, action) {
      state.insightsTypes = { ...DEFAULT_INSIGHT_TYPES, ...state.insightsTypes, [action.payload.type]: action.payload.on };
      savePreferences(state);
    },
    setInsightsRampMethod(state, action) {
      if (!RAMP_METHODS.includes(action.payload)) return;
      state.insightsRampMethod = action.payload;
      savePreferences(state);
    },
    markVersionSeen(state, action) {
      state.seenVersion = action.payload; // e.g. "0.6.3.0"
      savePreferences(state);
    },
    setSidebarCollapsed(state, action) {
      state.sidebarCollapsed = action.payload;
      savePreferences(state);
    },
  },
});

export const {
  setNewUsersOverTimeDays,
  setActiveUsersDays,
  setSelectedResource,
  setBlurSensitive,
  setTheme,
  setPreferredModel,
  setInsightsEnabled,
  setInsightsStrongOnly,
  setInsightsShowPast,
  setInsightsType,
  setInsightsRampMethod,
  markVersionSeen,
  setSidebarCollapsed,
} = preferencesSlice.actions;

export const selectNewUsersOverTimeDays = (state) =>
  state.preferences.newUsersOverTimeDays;
// Fallback to the default when the key is absent — loadPreferences() returns
// the stored object verbatim, so users with older persisted preferences won't
// have this key yet.
export const selectActiveUsersDays = (state) =>
  state.preferences.activeUsersDays ?? 30;
export const selectSelectedResourceId = (state) =>
  state.preferences.resource; // Selector for the selected resource ID
export const selectSelectedResource = createSelector(
  [selectSelectedResourceId, (state) => state.user.resources],
  (selectedResourceId, resources) => {
    if (!selectedResourceId || !resources) return null;
    return resources.find((resource) => resource.id === selectedResourceId) || null;
  }
);
// Only superusers (admins) may unmask participant identities. For everyone
// else (market owners, read-only owners, forecasters) the selector reports
// `true` regardless of the stored preference, forcing alias-only display.
export const selectBlurSensitive = (state) =>
  state.auth?.isAdmin ? state.preferences.blurSensitive : true;
export const selectTheme = (state) => state.preferences.theme;
export const selectPreferredModel = (state) =>
  state.preferences.preferredModel;
export const selectSeenVersion = (state) => state.preferences.seenVersion;
export const selectSidebarCollapsed = (state) => state.preferences.sidebarCollapsed ?? false;
// Older stored preferences lack the insights keys, hence the fallbacks.
export const selectInsightsEnabled = (state) => state.preferences.insightsEnabled ?? false;
export const selectInsightsStrongOnly = (state) => state.preferences.insightsStrongOnly ?? true;
export const selectInsightsShowPast = (state) => state.preferences.insightsShowPast ?? false;
export const selectInsightsRampMethod = (state) => state.preferences.insightsRampMethod ?? 'forecast';
// Memoised so useSelector gets the same object until the stored types change.
export const selectInsightsTypes = createSelector(
  [(state) => state.preferences.insightsTypes],
  (types) => ({ ...DEFAULT_INSIGHT_TYPES, ...(types || {}) })
);

export default preferencesSlice.reducer;
